import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import AlvnaBrand from "../components/AlvnaBrand";
import HelpButton from "../components/HelpButton";
import useRestaurantName from "../useRestaurantName";
import useClockState from "../useClockState";
import TabbedStation from "../components/TabbedStation";
import ClockBanner from "../components/ClockBanner";
import { endpoints } from "../apiConfig";
import { getAuthHeader, getOperatorIdentity, getOperatorLoginRedirect } from "../session";
import { colors, fonts, space, radius } from "../theme";

const authHeader = () => getAuthHeader();
const POLL_INTERVAL_MS = 4000;

const COLUMNS = [
  { key: "new", label: "New", color: colors.statusNew, nextStatus: "in_progress", actionLabel: "Start" },
  { key: "in_progress", label: "In Progress", color: colors.statusProgress, nextStatus: "ready", actionLabel: "Ready" },
  { key: "ready", label: "Ready", color: colors.statusReady, nextStatus: null, actionLabel: null },
];

function elapsedMinutes(dateStr) {
  if (!dateStr) return 0;
  return Math.max(0, Math.floor((Date.now() - new Date(dateStr).getTime()) / 60000));
}

function KitchenBoard() {
  const [tickets, setTickets] = useState([]);
  const [staff, setStaff] = useState(null);
  const restaurantName = useRestaurantName();
  const { clock, blocked: readOnly } = useClockState();
  const pollRef = useRef(null);

  useEffect(() => {
    if (!getAuthHeader().Authorization) {
      window.location.href = getOperatorLoginRedirect("kitchen");
      return;
    }
    setStaff(getOperatorIdentity());
    loadTickets();
    pollRef.current = setInterval(loadTickets, POLL_INTERVAL_MS);
    return () => clearInterval(pollRef.current);
  }, []);

  const loadTickets = () => {
    axios.get(endpoints.kitchenTickets, { headers: authHeader() })
      .then((res) => setTickets(res.data))
      .catch((err) => {
        if (err.response?.status === 401) window.location.href = getOperatorLoginRedirect("kitchen");
      });
  };

  const advanceItem = async (itemId, nextStatus) => {
    // Optimistic update so the tap feels instant, matching order.alvna.com.
    setTickets((prev) =>
      prev.map((t) => ({
        ...t,
        items: t.items.map((i) => (i.id === itemId ? { ...i, status: nextStatus } : i)),
      }))
    );
    try {
      await axios.patch(endpoints.orderItemStatus(itemId), { status: nextStatus }, { headers: authHeader() });
    } catch {
      loadTickets(); // revert to server truth if the update failed
    }
  };

  const logout = () => {
    const redirect = getOperatorLoginRedirect("kitchen");
    sessionStorage.removeItem("staff_token");
    sessionStorage.removeItem("staff");
    sessionStorage.removeItem("staff_tenant");
    localStorage.removeItem("token");
    localStorage.removeItem("tenant");
    window.location.href = redirect;
  };

  return (
    <div style={styles.page}>
      {restaurantName && <div className="restaurant-title" style={styles.restaurantTitle} data-testid="restaurant-title">{restaurantName}</div>}
      <ClockBanner clock={clock} blocked={readOnly} />
      <div style={styles.header}>
        <h1 style={styles.title}>Kitchen <HelpButton page="kitchen" dark /></h1>
        <div style={{ display: "flex", alignItems: "center", gap: space[3] }}>
          {staff && <span style={styles.staffName}>{staff.name}</span>}
          {(staff?.role === "manager" || staff?.role === "owner") && (
            <a href="/manager-home" style={styles.managerHomeLink}>Manager Home</a>
          )}
          <button style={styles.logoutBtn} onClick={logout}>Log Out</button>
        </div>
      </div>

      <div style={styles.board}>
        {COLUMNS.map((col) => {
          const cardsInColumn = [];
          tickets.forEach((ticket) => {
            const itemsInStage = ticket.items.filter((i) => i.status === col.key);
            if (itemsInStage.length > 0) {
              cardsInColumn.push({ ...ticket, items: itemsInStage });
            }
          });

          return (
            <div key={col.key} style={styles.column}>
              <div style={{ ...styles.columnHeader, borderColor: col.color }}>
                <span style={styles.columnHeaderLeft}>
                  <span style={{ ...styles.dot, background: col.color }} />
                  {col.label}
                </span>
                <span style={styles.columnCount}>{cardsInColumn.length}</span>
              </div>
              <div style={styles.columnBody}>
                {cardsInColumn.map((ticket) => (
                  <div key={`${col.key}-${ticket.order_id}`} style={{ ...styles.ticketCard, borderColor: col.color }}>
                    <div style={styles.ticketHeader}>
                      <span>{ticket.table_label}</span>
                      <span style={styles.ticketTime}>{elapsedMinutes(ticket.order_created_at)} min</span>
                    </div>
                    {ticket.items.map((item) => (
                      <div key={item.id} style={{ ...styles.itemRow, borderLeft: `4px solid ${col.color}` }}>
                        <div>
                          <div style={styles.itemName}>{item.quantity}× {item.item_name}</div>
                          {item.notes && <div style={styles.itemNotes}>{item.notes}</div>}
                        </div>
                        {col.actionLabel ? (
                          <button
                            style={styles.actionBtn}
                            onClick={() => advanceItem(item.id, col.nextStatus)}
                            disabled={readOnly}
                          >
                            {col.actionLabel}
                          </button>
                        ) : (
                          <span style={styles.waitingPickup}>Waiting pickup</span>
                        )}
                      </div>
                    ))}
                  </div>
                ))}
                {cardsInColumn.length === 0 && (
                  <p style={styles.emptyText}>Nothing here</p>
                )}
              </div>
            </div>
          );
        })}
      </div>

      <div style={styles.brandFooter}>
        <AlvnaBrand variant="compact" />
      </div>
    </div>
  );
}

const styles = {
  page: { minHeight: "100vh", background: colors.darkBg, padding: space[5] },
  header: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: space[5], flexWrap: "wrap", gap: space[2] },
  title: { fontFamily: fonts.display, color: colors.darkText, fontSize: 22, margin: 0, display: "flex", alignItems: "center" },
  staffName: { fontFamily: fonts.body, color: colors.textMuted, fontSize: 13 },
  logoutBtn: { background: "none", border: `1px solid ${colors.darkBorder}`, color: colors.darkText, borderRadius: radius.sm, padding: "10px 16px", fontSize: 14, cursor: "pointer", minHeight: 44, display: "flex", alignItems: "center" },
  managerHomeLink: { background: "none", border: `1px solid ${colors.darkBorder}`, color: colors.brandBlue, borderRadius: radius.sm, padding: "10px 16px", fontSize: 14, textDecoration: "none", minHeight: 44, display: "flex", alignItems: "center" },
  restaurantTitle: { textAlign: "center", fontFamily: fonts.display, fontWeight: 700, fontSize: 20, lineHeight: 1.25, color: colors.darkText, marginBottom: space[3], wordBreak: "break-word" },
  board: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: space[4] },
  column: { display: "flex", flexDirection: "column", minHeight: 200 },
  columnHeader: {
    fontFamily: fonts.display, color: colors.darkText, fontSize: 15, fontWeight: 600,
    display: "flex", justifyContent: "space-between", alignItems: "center",
    padding: `${space[2]} ${space[2]}`, borderBottom: "3px solid", marginBottom: space[3],
  },
  columnHeaderLeft: { display: "flex", alignItems: "center", gap: space[2] },
  dot: { width: 8, height: 8, borderRadius: "50%", display: "inline-block" },
  columnCount: { fontFamily: fonts.body, fontSize: 12, color: colors.textMuted, background: colors.darkSurface, borderRadius: radius.pill, padding: "2px 8px" },
  columnBody: { display: "flex", flexDirection: "column", gap: space[3] },
  ticketCard: { background: colors.darkSurface, border: "1px solid", borderRadius: radius.md, padding: space[3] },
  ticketHeader: { display: "flex", justifyContent: "space-between", fontFamily: fonts.display, color: colors.darkText, fontSize: 14, fontWeight: 600, marginBottom: space[2], paddingBottom: space[2], borderBottom: `1px solid ${colors.darkBorder}` },
  ticketTime: { fontFamily: fonts.body, color: colors.textMuted, fontSize: 12, fontWeight: 400 },
  itemRow: {
    display: "flex", justifyContent: "space-between", alignItems: "center",
    background: colors.darkBg, borderRadius: radius.sm, padding: space[2], marginBottom: space[2],
  },
  itemName: { fontFamily: fonts.body, color: colors.darkText, fontSize: 13 },
  itemNotes: { fontFamily: fonts.body, color: colors.textMuted, fontSize: 11, fontStyle: "italic" },
  actionBtn: {
    background: colors.brandGradient, border: "none", color: "#fff", fontSize: 14, fontWeight: 600,
    padding: "12px 18px", borderRadius: radius.pill, cursor: "pointer", whiteSpace: "nowrap", minHeight: 44,
  },
  waitingPickup: { fontFamily: fonts.body, color: colors.statusReady, fontSize: 12, fontWeight: 600, whiteSpace: "nowrap" },
  emptyText: { fontFamily: fonts.body, color: colors.textMuted, fontSize: 12, textAlign: "center", padding: space[3] },
  brandFooter: { marginTop: space[5], textAlign: "center" },
};

export default function KitchenPage() {
  return <TabbedStation Board={KitchenBoard} title="Kitchen" dark={true} />;
}
